import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import supabaseService from '../services/supabaseService';
import backblazeService from '../services/backblazeService';
import {
  FiArrowRight,
  FiUploadCloud,
  FiX,
  FiSend,
  FiShield,
  FiCheckCircle,
  FiClock,
  FiXCircle,
  FiRefreshCw,
} from 'react-icons/fi';
import { toast } from 'react-hot-toast';

const STATUS_CONFIG = {
  pending: {
    icon: FiClock,
    iconColor: 'text-amber-500',
    bg: 'bg-amber-50',
    border: 'border-amber-100',
    title: 'طلبك قيد المراجعة',
    desc: 'تم استلام طلب الشحن وهو الآن قيد المراجعة من قبل الإدارة. سيتم إضافة الرصيد لمحفظتك فور الموافقة عليه. لا يمكنك إرسال طلب جديد حتى تتم معالجة هذا الطلب.',
    badge: 'قيد المراجعة',
    badgeBg: 'bg-amber-100 text-amber-700',
  },
  accepted: {
    icon: FiCheckCircle,
    iconColor: 'text-emerald-500',
    bg: 'bg-emerald-50',
    border: 'border-emerald-100',
    title: 'تمت الموافقة على آخر طلب',
    desc: 'تمت الموافقة على طلب الشحن السابق وإضافة الرصيد لمحفظتك بنجاح. يمكنك إرسال طلب شحن جديد في أي وقت.',
    badge: 'مقبول',
    badgeBg: 'bg-emerald-100 text-emerald-700',
  },
  rejected: {
    icon: FiXCircle,
    iconColor: 'text-red-500',
    bg: 'bg-red-50',
    border: 'border-red-100',
    title: 'تم رفض طلبك السابق',
    desc: 'تم رفض طلب الشحن السابق. تأكد من صحة صورة الوصل والمبلغ ثم يمكنك إرسال طلب جديد.',
    badge: 'مرفوض',
    badgeBg: 'bg-red-100 text-red-700',
  },
};

export default function RechargeReceiptScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [amount, setAmount] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [loadingHistory, setLoadingHistory] = useState(true);
  const [recharges, setRecharges] = useState([]); // كل الطلبات، الأحدث أولاً

  useEffect(() => {
    loadRecharges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadRecharges = async () => {
    if (!user) return;
    setLoadingHistory(true);
    try {
      const data = await supabaseService.getWalletRecharges(user.id);
      setRecharges(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // آخر طلب (الأحدث) هو المعيار الوحيد لمنع الرفع
  const latestRequest = recharges.length > 0 ? recharges[0] : null;
  const latestStatus = latestRequest?.status || null;
  const cfg = latestStatus ? STATUS_CONFIG[latestStatus] : null;

  // ✅ يُمنع الرفع فقط إذا كان آخر طلب لا يزال قيد المراجعة (pending)
  // accepted و rejected كلاهما يسمحان بإرسال طلب جديد
  const canUpload = latestStatus !== 'pending';

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('يرجى اختيار صورة صالحة للوصل');
        return;
      }
      setReceiptFile(file);
      setReceiptPreview(URL.createObjectURL(file));
    }
  };

  const removeFile = () => {
    setReceiptFile(null);
    setReceiptPreview('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canUpload) return;

    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount < 100) {
      toast.error('أدخل مبلغاً صحيحاً (100 دج على الأقل)');
      return;
    }

    if (!receiptFile) {
      toast.error('يرجى رفع صورة وصل التحويل');
      return;
    }

    setSubmitting(true);
    const toastId = toast.loading('جاري رفع الوصل وإرسال الطلب...');

    try {
      const receiptUrl = await backblazeService.uploadImage(receiptFile, 'recharges');
      if (!receiptUrl) throw new Error('فشل رفع صورة الوصل');

      await supabaseService.createWalletRecharge({
        userId: user.id,
        amount: numAmount,
        receiptUrl,
      });

      toast.success('تم إرسال طلب الشحن للمراجعة بنجاح', { id: toastId });

      // إعادة تعيين النموذج وتحديث السجل والحالة في نفس الصفحة
      setAmount('');
      setReceiptFile(null);
      setReceiptPreview('');
      await loadRecharges();
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'حدث خطأ أثناء إرسال الطلب', { id: toastId });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="h-screen w-full bg-gray-50 font-sans flex flex-col overflow-hidden" dir="rtl">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 lg:px-10 py-3.5 flex items-center gap-3 shadow-sm shrink-0">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors">
          <FiArrowRight className="text-gray-700" size={20} />
        </button>
        <h1 className="text-lg font-bold text-gray-900">شحن عبر بريدي موب</h1>
        <button
          onClick={loadRecharges}
          className="mr-auto p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          title="تحديث الحالة"
        >
          <FiRefreshCw className={`text-gray-500 ${loadingHistory ? 'animate-spin' : ''}`} size={16} />
        </button>
      </div>

      {/* Main content — fills remaining height, no page scroll */}
      <div className="flex-1 min-h-0 px-6 lg:px-10 py-5 lg:py-8">
        <div className="h-full max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">

          {/* ── العمود الأيمن: الحالة + التعليمات + السجل ── */}
          <div className="lg:col-span-2 flex flex-col gap-5 min-h-0">

            {/* بطاقة حالة آخر طلب */}
            {cfg && (
              <div className={`${cfg.bg} border ${cfg.border} p-4 rounded-xl flex items-start gap-3 shrink-0`}>
                <cfg.icon className={`${cfg.iconColor} mt-0.5 shrink-0`} size={20} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <h3 className="text-sm font-bold text-gray-900">{cfg.title}</h3>
                    <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 ${cfg.badgeBg}`}>
                      {cfg.badge}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 leading-relaxed">{cfg.desc}</p>
                  {latestRequest?.amount != null && (
                    <p className="text-[11px] text-gray-400 mt-2">
                      المبلغ: <span className="font-bold text-gray-600">{latestRequest.amount} دج</span>
                      {latestRequest.created_at && (
                        <>
                          {' '}• {new Date(latestRequest.created_at).toLocaleDateString('ar-DZ')}
                        </>
                      )}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* تنبيه التعليمات - يظهر فقط عند إمكانية الرفع */}
            {canUpload && (
              <div className="bg-blue-50 border border-blue-100 p-4 rounded-xl flex items-start gap-3 shrink-0">
                <FiShield className="text-blue-400 mt-0.5 shrink-0" size={18} />
                <p className="text-xs text-blue-800 leading-relaxed">
                  قم بتحويل المبلغ عبر تطبيق بريدي موب إلى الحساب المخصص، ثم ارفع صورة وصل التحويل هنا ليتم التحقق منه وإضافة الرصيد لمحفظتك.
                </p>
              </div>
            )}

            {/* ── سجل طلبات الشحن ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden flex-1 min-h-0 flex flex-col">
              <div className="p-4 flex justify-between items-center border-b border-gray-100 shrink-0">
                <h3 className="font-bold text-gray-800">سجل طلبات الشحن</h3>
                <button onClick={loadRecharges} className="text-gray-400 hover:text-gray-600">
                  <FiRefreshCw className={loadingHistory ? 'animate-spin' : ''} size={16} />
                </button>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto">
                {loadingHistory ? (
                  <div className="p-8 text-center text-gray-400">جاري التحميل...</div>
                ) : recharges.length === 0 ? (
                  <div className="p-8 text-center text-gray-400">
                    <FiClock className="mx-auto mb-2" size={24} />
                    <p className="text-sm">لا توجد طلبات شحن سابقة</p>
                  </div>
                ) : (
                  <div>
                    {recharges.map((r) => {
                      const rowCfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.pending;
                      return (
                        <div key={r.id} className="p-4 flex items-center justify-between border-b border-gray-50 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${rowCfg.bg}`}>
                              <rowCfg.icon className={rowCfg.iconColor} size={18} />
                            </div>
                            <div>
                              <p className="font-bold text-sm text-gray-800">{r.amount} دج</p>
                              <p className="text-[11px] text-gray-400">
                                {r.created_at ? new Date(r.created_at).toLocaleDateString('ar-DZ') : '—'}
                              </p>
                            </div>
                          </div>
                          <span className={`text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${rowCfg.badgeBg}`}>
                            {rowCfg.badge}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── العمود الأيسر: نموذج الشحن ── */}
          <div className="lg:col-span-3 flex flex-col min-h-0">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 lg:p-8 shadow-sm flex-1 min-h-0 overflow-y-auto">
              {!canUpload ? (
                // النموذج معطّل فقط أثناء pending
                <div className="h-full flex flex-col items-center justify-center text-center py-6">
                  <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-4">
                    <FiClock className="text-amber-500" size={28} />
                  </div>
                  <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                    لا يمكنك إرسال طلب شحن جديد حتى تتم معالجة طلبك الحالي.
                  </p>
                  <button
                    onClick={() => navigate('/wallet')}
                    className="bg-gray-900 text-white py-3 px-8 rounded-xl font-bold"
                  >
                    العودة إلى المحفظة
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="h-full flex flex-col justify-center space-y-5">
                  {/* المبلغ */}
                  <div className="space-y-2 text-right">
                    <label className="block text-sm font-bold text-gray-700">المبلغ المُحوّل (دج)</label>
                    <input
                      type="number"
                      min="100"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder="مثال: 1000"
                      className="w-full p-3.5 border border-gray-300 rounded-xl text-lg font-bold text-left focus:border-gray-900 focus:ring-1 focus:ring-gray-900 outline-none transition"
                      required
                      disabled={submitting}
                    />
                    <p className="text-[11px] text-gray-400">الحد الأدنى للشحن هو 100 دج.</p>
                  </div>

                  {/* رفع الوصل */}
                  <div className="space-y-2 text-right">
                    <label className="block text-sm font-bold text-gray-700">صورة وصل التحويل</label>

                    {!receiptPreview ? (
                      <label className="border-2 border-dashed border-gray-300 hover:border-gray-900 rounded-2xl p-6 text-center cursor-pointer transition bg-gray-50 hover:bg-gray-100 flex flex-col items-center justify-center gap-2 min-h-36">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileChange}
                          disabled={submitting}
                        />
                        <FiUploadCloud className="text-gray-400 w-10 h-10" />
                        <span className="text-sm font-bold text-gray-700">اضغط لرفع صورة الوصل</span>
                        <span className="text-xs text-gray-400">صيغ الصور المدعومة: PNG, JPG, JPEG</span>
                      </label>
                    ) : (
                      <div className="relative rounded-2xl overflow-hidden border border-gray-200 bg-gray-50 p-2.5">
                        <img
                          src={receiptPreview}
                          alt="Receipt Preview"
                          className="max-h-48 w-full object-contain rounded-xl"
                        />
                        <button
                          type="button"
                          onClick={removeFile}
                          className="absolute top-4 left-4 p-2 bg-red-600 text-white rounded-full hover:bg-red-700 transition shadow-md"
                          disabled={submitting}
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={submitting || !amount || !receiptFile}
                    className="w-full bg-gray-900 text-white py-3.5 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-md hover:shadow-lg mt-2"
                  >
                    {submitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>جاري الإرسال...</span>
                      </>
                    ) : (
                      <>
                        <FiSend size={16} />
                        <span>إرسال الطلب للمراجعة</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}